<template>
  <b-table
    :items="members"
    :fields="fields"
    :head-variant="'dark'"
    :sort-by="'lastname'"
    :sort-direction="'asc'"
    :sort-compare="sortCompare"
    :busy="loading"
    thead-tr-class="header-table"
    striped
    hover
    small
  >
    <template #cell(role)="data">
      <div
        v-if="isEditingRole"
        class="d-flex align-items-center"
      >
        <b-form-select
          :options="usergroupRoles.value"
          :value="data.item.role.choice"
          size="sm"
          value-field="choice"
          text-field="label"
          @change="$event => editUserRole(data.item, $event)"
        />
        <b-icon
          icon="x"
          variant="dark"
          font-scale="1.7"
          @click.stop="isEditingRole = false"
        />
      </div>
      <div v-else>
        {{ data.item.role.label }}
        <b-icon
          icon="pencil-fill"
          variant="primary"
          @click.stop="isEditingRole = true"
        />
      </div>
    </template>
    <template #cell(status)="data">
      <div class="text-center">
        <b-button
          v-if="data.item.status.value === 1"
          variant="danger"
          size="sm"
          style="padding: 0.1rem 0.3rem; font-size: 0.8rem;"
          @click.stop="validateUser(data.item)"
        >
          {{ $t('buttons.validate') }}
        </b-button>
        <b-badge
          v-else
          v-b-tooltip.hover
          :variant="data.item.status.value === 2
            ? 'success'
            : data.item.status.value === 0
              ? 'secondary'
              : 'dark'"
          :title="data.item.status.value === 0 ? 'L\'utilisateur s\'est inscrit mais n\'a pas encore confirmé' : null"
          style="padding: 0.25rem;"
        >
          {{ data.item.status.label }}
        </b-badge>
      </div>
    </template>
    <template #cell(date_joined)="data">
      {{ formatMemberDate(data.item.date_joined) }}
    </template>
    <template #cell(last_login)="data">
      {{ formatMemberDate(data.item.last_login) }}
    </template>
    <template #cell(is_superuser)="data">
      <div class="text-center">
        <b-icon
          v-if="data.item.is_superuser"
          icon="check"
          variant="success"
          font-scale="1.5"
        />
        <b-icon
          v-else
          icon="x"
          variant="danger"
          font-scale="1.5"
        />
      </div>
    </template>
    <template #cell(remove)="data">
      <div class="text-center">
        <b-icon
          icon="trash-fill"
          variant="danger"
          font-scale="1.5"
          @click.stop="removeUser(data.item)"
        />
      </div>
    </template>
  </b-table>
</template>

<script>
import i18n from '@/common/i18n/i18n.js';
import {
  organisationAPI,
  usergroupAPI,
  usersAPI
} from '@/api';
import { ErrorService } from '@/services/error-service.js';

export default {
  name: 'OrganisationMembersTable',

  props: {
    organisation: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      members: [],
      isEditingRole: false,
    };
  },

  computed: {
    fields() {
      const fields = [
        {
          key: 'id',
          label: 'ID',
          apiName: 'id',
          sortable: true,
          width: '',
          centered: false
        },
        {
          key: 'lastname',
          label: this.$t('organisationDetail.membersTable.lastname'),
          apiName: 'user__last_name',
          sortable: true,
          width: '',
          centered: false
        },
        {
          key: 'firstname',
          label: this.$t('organisationDetail.membersTable.firstname'),
          apiName: 'user__first_name',
          sortable: true,
          width: '',
          centered: false
        },
        {
          key: 'email',
          label: this.$t('organisationDetail.membersTable.email'),
          apiName: 'user__email',
          sortable: true,
          width: '',
          centered: false
        },
        {
          key: 'username',
          label: this.$t('organisationDetail.membersTable.username'),
          apiName: 'user__username',
          sortable: true,
          width: '',
          centered: false
        },
        {
          key: 'role',
          label: this.$t('organisationDetail.membersTable.role'),
          apiName: 'role',
          sortable: true,
          width: '',
          centered: false
        },
        {
          key: 'date_joined',
          label: this.$t('organisationDetail.membersTable.registrationDate'),
          apiName: 'date_joined',
          sortable: true,
          width: '15%',
          centered: true
        },
        {
          key: 'last_login',
          label: this.$t('organisationDetail.membersTable.lastLoginDate'),
          apiName: 'user__last_login',
          sortable: true,
          width: '15%',
          centered: true
        },
        {
          key: 'status',
          label: this.$t('organisationDetail.membersTable.status'),
          apiName: 'status',
          sortable: true,
          width: '8%',
          centered: true
        },
        ...this.$config.isSaas ? [] : [{
          key: 'is_superuser',
          label: this.$t('organisationDetail.membersTable.admin'),
          apiName: 'is_superuser',
          sortable: false,
          width: '6%',
          centered: true
        }],
        {
          key: 'remove',
          label: this.$t('organisationDetail.membersTable.remove'),
          sortable: false,
          width: '15%',
          centered: true
        }
      ];
      return fields.map(field => {
        return {
          ...field,
          tdClass: 'align-middle'
        };
      });
    },
  },

  watch: {
    loading(newValue) {
      this.$emit('update:loading', newValue);
    },
    currentUsergroupMembers: {
      handler() {
        this.setMembers();
      },
      deep: true
    },
    usergroupRoles: {
      handler() {
        this.setMembers();
      },
      deep: true,
    }
  },

  mounted() {
    this.setMembers();
  },

  methods: {
    formatMemberDate(value) {
      if (!value) {
        return '';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleString(this.$i18n.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    toTimestamp(value) {
      if (!value) {
        return 0;
      }
      const time = Date.parse(value);
      return Number.isNaN(time) ? 0 : time;
    },

    sortCompare(a, b, key) {
      if (key !== 'date_joined' && key !== 'last_login') {
        return false;
      }
      const aTime = this.toTimestamp(a[key]);
      const bTime = this.toTimestamp(b[key]);
      if (aTime === bTime) {
        return 0;
      }
      return aTime < bTime ? -1 : 1;
    },

    setMembers() {
      this.members = this.currentUsergroupMembers.value.map(el => {
        return {
          id: el.user.id,
          groupmember:el,
          lastname: el.user.last_name,
          firstname: el.user.first_name,
          email: el.user.email,
          username: el.user.username,
          role_value: el.role.value,
          role: this.usergroupRoles.value.some(item => item.choice === el.role.value) ?
            this.usergroupRoles.value.find(item => item.choice === el.role.value) :
            '',
          date_joined: el.date_joined || '',
          last_login: el.user.last_login || '',
          status: el.status,
          is_superuser: el.user.is_superuser ? el.user.is_superuser : false,
          remove: true
        };
      });
    },

    async removeUser(user) {
      try {
        const userId = this.currentUsergroupMembers.value.find(el => el.user.id === user.id).id;
        const usergroupId = this.organisation.usergroup.id;

        this.loading = true;

        const index = this.currentUsergroupMembers.value.findIndex(el => el.user.id === user.id);
        this.currentUsergroupMembers.value.splice(index, 1);

        const response = await organisationAPI.removeUserFromOrganisation(usergroupId, userId);
        if (response) {
          ErrorService.onSuccess(response, i18n.messages[i18n.locale].messages.success.user.deleted.replace('{user}', `${user.firstname} ${user.lastname}`));
        } else {
          ErrorService.onError(response, i18n.messages[i18n.locale].messages.error.user.removed.replace('{user}', `${user.firstname} ${user.lastname}`));
        }
        this.$emit('refresh');
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async validateUser(user) {
      this.loading = true;
      try {
        // let data = { status: 2 };
        // await usersAPI.patchUser( user.id,  data);
        let data={
          role: user.role_value,
          status: 2,
          user_id: user.id
        };
        await usergroupAPI.updateUsergroupMember(
          this.organisation.usergroup.id,
          user.groupmember.id,
          data,
        );
        await usergroupAPI.getUsergroupMembers(this.organisation.usergroup.id);
      } catch (err) {
        ErrorService.onError(
          err,
          i18n.messages[i18n.locale].messages.error.user.updated.replaceAll('{user}', `${user.firstname} ${user.lastname}`)
        );
      } finally {
        this.loading = false;
      }
    },

    async editUserRole(user, role) {
      this.isEditingRole = false;
      this.loading = true;
      try {
        const userData = this.currentUsergroupMembers.value.find(el => el.user.id === user.id);
        const data = {
          role: role,
          status: userData.status.value,
          user_id: userData.user.id
        };
        await usergroupAPI.updateUsergroupMember(
          this.organisation.usergroup.id,
          userData.id,
          data,
        );
        ErrorService.onSuccess(
          null,
          i18n.messages[i18n.locale].messages.success.userRole.updated
            .replaceAll(
              '{user}',
              `${user.firstname} ${user.lastname}`
            )
            .replaceAll(
              '{usergroup}',
              `${this.organisation.usergroup.display_name}`
            )
        );
        await usergroupAPI.getUsergroupMembers(this.organisation.usergroup.id);
      } catch (err) {
        ErrorService.onError(
          err,
          i18n.messages[i18n.locale].messages.error.userRole.updated
            .replaceAll(
              '{user}',
              `${user.firstname} ${user.lastname}`
            )
            .replaceAll(
              '{usergroup}',
              `${this.organisation.usergroup.display_name}`
            )
        );
      } finally {
        this.loading = false;
      }
    },
  }
};
</script>

<style lang="less" scoped>
table {
  margin-top: 0 !important;
}
.bi-trash-fill:hover,
.bi-pencil-fill:hover {
  transform: scale(1.1);
}
.bi-pencil-fill {
  margin-left: 0.5em;
}

/* b-table renders thead in the child tree, so scoped rules need ::v-deep */
::v-deep .header-table th {
  padding: 0.2rem 0.6rem;
  font-size: 0.8rem;
  vertical-align: middle;
}
</style>